import { useCallback, useEffect, useState } from 'react';
import useModalToggle from './useModalToggle';
import is from '@/utils/is';
import useTriggerRequest from './useTriggerRequest';
import fetchAndMergeProductsWithPrice from '@/utils/fetchAndMergeProductsWithPrice';

type PLP = {
  products: any[];
  filters: any;
  resultCount: number;
  pageCount: number;
};

export const PLP_SIZE = 8;

const usePLP = (
  plp: PLP,
  service: Function,
  q: string | null,
  extraFilters: any,
  size = PLP_SIZE,
) => {
  const [plpList, setPLPList] = useState(plp);
  const { open, openModal, closeModal } = useModalToggle(false);
  const [modalType, setModalType] = useState<'filter' | 'sort' | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [applyFliter, setApplyFliter] = useState({});
  const [applySortByFilter, setApplySortByFilter] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const fetch = (...args: any) => service.apply(null, args);
  const searchResults = useTriggerRequest({ fetch });

  useEffect(() => {
    setPLPList(plp);
  }, [plp]);

  const productFilterServices = useCallback(
    async (filters: any, page: number, sort: any) => {
      setIsLoading(true);
      const searchResultsData = await searchResults.promisifyTrigger({
        q,
        page,
        size,
        filters: { ...extraFilters, ...filters },
        sort: sort ? { key: sort.key?.toUpperCase(), order: sort.order } : {},
      });
      const { products, filters: newFilters, pageCount, resultCount } = searchResultsData?.data;

      const productsWithPriceData = products?.length
        ? await fetchAndMergeProductsWithPrice(products)
        : [];
      const plpData = {
        products:
          page !== 1 ? [...plpList?.products, ...productsWithPriceData] : productsWithPriceData,
        filters: newFilters,
        pageCount,
        resultCount,
      };
      setPLPList(plpData);
      setIsLoading(false);
    },
    [plpList?.products, q, searchResults, extraFilters, size],
  );

  const viewMoreProduct = useCallback(async () => {
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    await productFilterServices(applyFliter, newPage, applySortByFilter);
  }, [applyFliter, applySortByFilter, currentPage, productFilterServices]);

  const onApplyFilter = useCallback(
    async (filterList: any) => {
      setCurrentPage(1);
      setApplyFliter(filterList);
      await productFilterServices(filterList, 1, applySortByFilter);
    },
    [applySortByFilter, productFilterServices],
  );

  const handleOpenModal = useCallback(
    (type: 'filter' | 'sort') => {
      setModalType(type);
      openModal();
    },
    [openModal],
  );

  const onApplySortBy = useCallback(
    async (sortByList: any) => {
      setCurrentPage(1);
      setApplySortByFilter(sortByList);
      await productFilterServices(applyFliter, 1, sortByList);
    },
    [applyFliter, productFilterServices],
  );

  const onResetFilter = useCallback(async () => {
    setCurrentPage(1);
    setApplyFliter({});
    await productFilterServices({}, 1, applySortByFilter);
  }, [applySortByFilter, productFilterServices]);

  const isEmptyProductsList = is.empty.array(plpList?.products);

  return {
    plpList,
    setPLPList,
    open,
    isEmptyProductsList,
    closeModal,
    handleOpenModal,
    modalType,
    currentPage,
    applySortByFilter,
    viewMoreProduct,
    isLoading,
    onApplyFilter,
    onApplySortBy,
    onResetFilter,
  };
};

export default usePLP;
